import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, DatePicker, Divider, Form, Input, message, Modal, Popconfirm, Result, Row, Select, Space, Table } from 'antd';
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, FilterOutlined, MailOutlined, PlusOutlined, SearchOutlined, UploadOutlined, WarningOutlined } from '@ant-design/icons';
import UploadOrderModal from './orderUploadModal';
import { Link, useNavigate } from 'react-router';
import { dateFormat, menuLinks, orderStatusesCode, selectOptionsTypes, tablePageSize } from '../../../utils/constants';
import { usePageTitle } from '../../../context/pageTitleContext';
import { useUserAuth } from '../../../context/UserContext';
import CustomerOrdersServices from '../../../services/customerOrdersServices';
import { getCountryLabel, handleDateConvertForApis } from '../../../utils/functions';
import dayjs from 'dayjs';
import { Can, permissions, roles } from '../../../utils/permissionUtils';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTemplates } from '../../../state/slices/utilsSlice';
import DynamicSelect from '../../../components/common/dynamicSelect';
import MultiUpdateOrders from './multiUpdateOrders';
import { ResultModalState } from '../../../types/commonTypes';
import ResultModal from '../../../components/common/resultModal';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";


const { RangePicker } = DatePicker;

const initialFilters = {
    customerGroupText: null,
    apO_DP_Grp_Name: null,
    soldTo: null,
    shipTo: null,
    material: null,
    model: null,
    productGroupDescription: null,
    materialType: null,
    poNumber: null,
    salesOrderNumber: null,
    storageLocation: null,
    plant: null,
    salesOrganization: null,
    orderStatus: null,

    confDeliveryDateFrom: null,
    confDeliveryDateTo: null,
    confDeliveryDateRange: null,

    reqDeliveryDateFrom: null,
    reqDeliveryDateTo: null,
    reqDeliveryDateRange: null,

    calendarDay_GIDate: null
};

const CustomerOrders = () => {
    const { t } = useTranslation();
    const { setPageTitle } = usePageTitle();
    const { user } = useUserAuth();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const userCountries = useSelector((state: any) => state.utils.userCountries);
    const orderStatuses = useSelector((state: any) => state.utils.orderStatuses);
    const [filters, setFilters] = useState(initialFilters);
    const [pageNumber, setPageNumber] = useState(1);
    const [sorterColumn, setSorterColumn] = useState("");
    // const [orderList, setOrderList] = useState([]);
    // const [loadingOrderList, setLoadingOrderList] = useState(false);
    const [uploadOrderModal, setUploadOrderModal] = useState(false);
    const navigate = useNavigate();
    // const [totalCountRecords, setTotalCountRecords] = useState(null);
    const [loadingRpaFiles, setLoadingRpaFiles] = useState(false);
    const [rpaCountryModal, setRpaCountryModal] = useState(false);
    const [searchPoNumberText, setSearchPoNumberText] = useState(null);
    const [searchSalesOrderNumberText, setSearchSalesOrderNumberText] = useState(null);
    const [activeFilteredColumn, setActiveFilteredColumn] = useState([]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    // const [sendingSelected, setSendingSelected] = useState(false);
    const rowKeyToOrderIdRef = useRef(new Map());
    const rowKeyToOrderItemsIdRef = useRef(new Map());
    const [multiUpdateOrdersModal, setMultiUpdateOrdersModal] = useState(false);

    const [resultModal, setResultModal] = useState<ResultModalState>({
        open: false,
        status: 'info',
        title: '',
        subTitle: '',
    });

    // useEffect(() => {
    //     getOrderList(filters);
    // }, [pageNumber, sorterColumn, filters]);

    useEffect(() => {
        if (!user) return;
        setPageTitle(t('page.customerOrders'));
        dispatch(fetchTemplates(user as any) as any);
    }, []);

    const refreshOrders = () => {
        queryClient.invalidateQueries({
            queryKey: ["orders"]
        });
    };


    const {
        data: orderData,
        isLoading: loadingOrderList,
    } = useQuery({
        queryKey: [
            "orders",
            filters,
            pageNumber,
            sorterColumn,
            user.id
        ],
    
        queryFn: async () => {
    
            const filterObj = {
                ...filters,
                pageNumber,
                sortColumn: sorterColumn,
                pageSize: tablePageSize,
                userID: user.id,
            };    
    
            const respData = await CustomerOrdersServices.getOrdersList(filterObj);    
    
            if (!respData.success) {
                throw new Error("Failed loading orders");
            }    
    
            const res = respData.responseObject.value.ordersList;  
            
            const buildRowKey = (item: any, idx: number) => {
                // Compact fingerprint from your most stable fields, then disambiguate with index
                const fp = `${item.id ?? ''}|${item.salesOrderNumber ?? ''}|${item.poNumber ?? ''}|${item.material ?? ''}|${item.shipToCode ?? ''}|${item.soldToCode ?? ''}`;
                // Use absolute index across pagination to avoid collisions between pages
                const absoluteIdx = ((pageNumber - 1) * tablePageSize) + idx;
                return `${fp}__at__${absoluteIdx}`;
            };    
    
            const preparedData = res.map((item:any, idx:number)=>({
                ...item,
                rowKey: buildRowKey(item, idx)
            }));

            const mId = rowKeyToOrderIdRef.current;
            const mOrderItemsId = rowKeyToOrderItemsIdRef.current;
            preparedData.forEach((r: any) => {
                mId.set(r.rowKey, String(r.id));
                const orderItemsId = r.orderItemsID ?? r.orderItemsId;
                if (orderItemsId !== undefined && orderItemsId !== null) {
                    mOrderItemsId.set(r.rowKey, String(orderItemsId));
                }
            });    
    
            return {
                orders: preparedData,
                total: res?.[0]?.totalCount ?? 0
            };
        },
    
        enabled: !!user.id,
    });

    // const getOrderList = async (orderFilters: any) => {

    //     let filterObj = orderFilters;
    //     filterObj["pageNumber"] = pageNumber;
    //     filterObj["sortColumn"] = sorterColumn;
    //     filterObj["pageSize"] = tablePageSize;
    //     filterObj["userID"] = user.id;

    //     setLoadingOrderList(true);
    //     try {
    //         const respData = await CustomerOrdersServices.getOrdersList(filterObj);

    //         if (respData.success) {
    //             const res = respData.responseObject.value.ordersList;

    //             const buildRowKey = (item: any, idx: number) => {
    //                 // Compact fingerprint from your most stable fields, then disambiguate with index
    //                 const fp = `${item.id ?? ''}|${item.salesOrderNumber ?? ''}|${item.poNumber ?? ''}|${item.material ?? ''}|${item.shipToCode ?? ''}|${item.soldToCode ?? ''}`;
    //                 // Use absolute index across pagination to avoid collisions between pages
    //                 const absoluteIdx = ((pageNumber - 1) * tablePageSize) + idx;
    //                 return `${fp}__at__${absoluteIdx}`;
    //             };

    //             const preparedData = res.map((item: any, idx: number) => ({
    //                 ...item,
    //                 rowKey: buildRowKey(item, idx),
    //             }));

    //             if (res && res.length > 0)
    //                 setTotalCountRecords(res[0].totalCount);

    //             setOrderList(preparedData);
    //             // persistently update global rowKey maps with current page rows
    //             const mId = rowKeyToOrderIdRef.current;
    //             const mOrderItemsId = rowKeyToOrderItemsIdRef.current;
    //             preparedData.forEach((r: any) => {
    //                 mId.set(r.rowKey, String(r.id));
    //                 const orderItemsId = r.orderItemsID ?? r.orderItemsId;
    //                 if (orderItemsId !== undefined && orderItemsId !== null) {
    //                     mOrderItemsId.set(r.rowKey, String(orderItemsId));
    //                 }
    //             });
    //         } else {
    //             message.error(t('messages.errorAccured'));
    //         }
    //     } catch (error) {
    //         message.error(t('messages.errorAccured'));
    //     } finally {
    //         setLoadingOrderList(false);
    //     }
    // };

    const onPageOrOrderChange = (pagination: any, filters: any, sorter: any) => {
        setPageNumber(pagination.current);
        if (sorter && sorter.field) {
            let sorterQuote = "";
            if (sorter.order === "descend") {
                sorterQuote = "[" + sorter.field + "]" + " " + "DESC";
            }
            if (sorter.order === "ascend") {
                sorterQuote = "[" + sorter.field + "]" + " " + "ASC";
            }
            setSorterColumn(sorterQuote);
        } else {
            setSorterColumn("");
        }
    };


    const onClearFilters = () => {
        setFilters(initialFilters);
        setPageNumber(1);
        // getOrderList(initialFilters);
        setActiveFilteredColumn([]);
    }

    const manageSelectedFilters = (filterName: string, filterValue: any) => {
        setActiveFilteredColumn((prev: any) => {
            if (filterValue) {
                if (!prev.includes(filterName)) {
                    return [...prev, filterName];
                } else {
                    return prev; // already in array → do nothing
                }
            } else {
                return prev.filter((item: any) => item !== filterName);
            }
        });
    };

    const handelFilterChange = (name: string, value: any) => {
        manageSelectedFilters(name, value);

        if (name === "confDeliveryDateRange") {
            if (!value) {
                setFilters((prev) => ({ ...prev, confDeliveryDateFrom: null }));
                setFilters((prev) => ({ ...prev, confDeliveryDateTo: null }));
                setFilters((prev) => ({ ...prev, confDeliveryDateRange: null }));
            }
            else {
                let startDate = handleDateConvertForApis(value[0].$d);
                let endDate = handleDateConvertForApis(value[1].$d);
                setFilters((prev: any) => ({ ...prev, confDeliveryDateFrom: startDate }));
                setFilters((prev: any) => ({ ...prev, confDeliveryDateTo: endDate }));
                setFilters((prev: any) => ({ ...prev, confDeliveryDateRange: value }));
            }
        }
        else if (name === "reqDeliveryDateRange") {
            if (!value) {
                setFilters((prev) => ({ ...prev, reqDeliveryDateFrom: null }));
                setFilters((prev) => ({ ...prev, reqDeliveryDateTo: null }));
                setFilters((prev) => ({ ...prev, reqDeliveryDateRange: null }));
            } else {
                let startDate = handleDateConvertForApis(value[0].$d);
                let endDate = handleDateConvertForApis(value[1].$d);
                setFilters((prev: any) => ({ ...prev, reqDeliveryDateFrom: startDate }));
                setFilters((prev: any) => ({ ...prev, reqDeliveryDateTo: endDate }));
                setFilters((prev: any) => ({ ...prev, reqDeliveryDateRange: value }));
            }
        }
        else if (name === "calendarDay_GIDate") {
            if (!value) {
                setFilters((prev) => ({ ...prev, calendarDay_GIDate: null }));
            } else {
                let date = handleDateConvertForApis(value.$d);
                setFilters((prev: any) => ({ ...prev, calendarDay_GIDate: date }));
            }
        }
        else {
            setFilters((prev) => ({ ...prev, [name]: value }));
        }
    }

    const onGenerateRpaFilesClick = () => {
        if (user.roleCode === roles.admin) {
            setRpaCountryModal(true);
        }
        else {
            generateRpaFiles();
        }
    }

    const onRpaCountryModalCancel = () => {
        setRpaCountryModal(false);
        form.resetFields();
    }

    const generateRpaFiles = async () => {
        let payload = {};
        let selectedCcountries = [];



        // if (user.roleCode === roles.admin) {
        //     const values = form.getFieldsValue();
        //     selectedCcountries = values.countries;
        // }
        // else {
        //     selectedCcountries.push(user.lastSelectedCountryID ?? null)
        // }

        // if (!Array.isArray(selectedCcountries) || selectedCcountries.length === 0) {
        //     message.error(t('messages.rpaCountryMissing'));
        //     return;
        // }

        
        if (!user.lastSelectedCountryID) {
            message.error(t('messages.rpaCountryMissing'));
            return;
        }

        // (payload as any)['CountryIDList'] = selectedCcountries.join(',');
        (payload as any)['CountryIDList'] = user.lastSelectedCountryID ?? null;
        (payload as any)['userID'] = user.id;

        setLoadingRpaFiles(true);
        try {
            const respData = await CustomerOrdersServices.generateRpaFiles(payload);
            if (respData.success) {
                message.success(t('messages.success'));
                refreshOrders();
                onRpaCountryModalCancel();
            }
            else {
                if (respData.errorDetail.errorCode === 2) {
                    setResultModal({
                        open: true,
                        status: 'warning',
                        title: t('messages.noRpaOrderToGenerate'),
                        subTitle: "",
                    });
                    onRpaCountryModalCancel();
                }
                else {                  
                    setResultModal({
                        open: true,
                        status: 'error',
                        title: t('messages.errorAccured'),
                        subTitle: "",
                    });
                }

            }
        } catch (error) {
            setResultModal({
                open: true,
                status: 'error',
                title: t('messages.errorAccured'),
                subTitle: "",
            });
        }
        finally {
            setLoadingRpaFiles(false);
            setRpaCountryModal(false);
        }
    }


    const handelSelectChangeFilter = (type: string, selectedOption: any) => {
        handelFilterChange(type, selectedOption?.value);
    };

    // const onRemoveOrder = async (id: string) => {
    //     try {
    //         const respData = await CustomerOrdersServices.deleteOrder(id);

    //         if (respData.success) {
    //             message.success(t('messages.success'));
    //             refreshOrders();
    //             setSelectedOrderIds([]);
    //             setSelectedItemIds([]);
    //             setSelectedRowKeys([]);
    //         }
    //         else {
    //             message.error(t('messages.errorAccured'));
    //         }

    //     } catch (error) {
    //         message.error(t('messages.errorAccured'));
    //     }
    // };

    const deleteOrderMutation = useMutation({
        mutationFn: CustomerOrdersServices.deleteOrder,
    
        onSuccess: (respData) => {
    
            if (!respData.success) {
                message.error(t("messages.errorAccured"));
                return;
            }
    
            message.success(t("messages.success"));
    
            setSelectedOrderIds([]);
            setSelectedItemIds([]);
            setSelectedRowKeys([]);
    
            queryClient.invalidateQueries({
                queryKey: ["orders"]
            });
    
        },
    
        onError: () => {
            message.error(t("messages.errorAccured"));
        }
    });

    const onRemoveOrder = (id: string) => {
        deleteOrderMutation.mutate(id);
    };


    const multiDeleteOrdersMutation = useMutation({
        mutationFn: CustomerOrdersServices.multiDeleteOrders,
        onSuccess: (respData) => {
            if (!respData.success) {
                message.error(t("messages.errorAccured"));
                return;
            }

            message.success(t("messages.success"));
            refreshOrders();
            setSelectedOrderIds([]);
            setSelectedItemIds([]);
            setSelectedRowKeys([]);
            refreshOrders();         
        },
        onError: () => {
            message.error(t("messages.errorAccured"));
        }
    });

    const onMultiDeleteOrders = () => {
        multiDeleteOrdersMutation.mutate(selectedOrderIds);
    };


    // const onMultiDeleteOrders = async () => {
    //     setSendingSelected(true);
    //     try {
    //         const respData = await CustomerOrdersServices.multiDeleteOrders(selectedOrderIds);

    //         if (respData.success) {
    //             message.success(t('messages.success'));
    //             refreshOrders();
    //             setSelectedOrderIds([]);
    //             setSelectedItemIds([]);
    //             setSelectedRowKeys([]);

    //         }
    //         else {
    //             message.error(t('messages.errorAccured'));
    //         }

    //     } catch (error) {
    //         message.error(t('messages.errorAccured'));
    //     }
    //     finally {
    //         setSendingSelected(false);
    //     }
    // };

    const columns = [
        {
            title: '',
            key: 'action',
            width: "30px",
            fixed: 'left',
            render: (text: any, record: any) => {

                return (
                    <Space size="middle" direction='horizontal' style={{ columnGap: 5 }}>

                            {selectedOrderIds.length > 0 ? (
                                <EditOutlined style={{ color: '#999', cursor: 'not-allowed' }} className='editIcon' />
                            ) : (
                                <Link to={{ pathname: `${menuLinks.customerOrderDetails}/view/${record.id}` }}> <EditOutlined style={{ color: '#023B72' }} className='editIcon' /> </Link>
                            )}


                        <Can permission={permissions.DELETE_ORDER}>
                            {record.deleteEnabled && (
                                <>
                                    <Divider type="vertical" style={{ marginInline: 10 }} />
                                    <Popconfirm
                                        placement="topRight"
                                        key="remove"
                                        title={t('messages.confirmationApprove')}
                                        icon={<WarningOutlined style={{ color: '#f7c924' }} />}
                                        okText={t('general.yes')}
                                        cancelText={t('general.no')}
                                        onConfirm={() => onRemoveOrder(record.id)}
                                        disabled={selectedOrderIds.length > 0}
                                    >
                                        <DeleteOutlined style={ selectedOrderIds.length > 0 ? { color: '#999', cursor: 'not-allowed' } : { color: '#023B72' }} />
                                    </Popconfirm>
                                </>
                            )}
                        </Can>
                    </Space>
                )
            }
        },
        {
            title: t('order.customerGroup'),
            dataIndex: 'customerGroupText',
            key: 'customerGroupText',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: ({ confirm }: { confirm: () => void }) => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.customerGroupText} value={filters.customerGroupText} onSelectChange={handelSelectChangeFilter} onChange={() => {}} location={null} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('customerGroupText') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.apO_DP_Grp'),
            dataIndex: 'apO_DP_Grp_Name',
            key: 'apO_DP_Grp_Name',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.apO_DP_Grp_Name} value={filters.apO_DP_Grp_Name} onSelectChange={handelSelectChangeFilter} onChange={() => {}} location={null} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('apO_DP_Grp_Name') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.soldToCode'),
            dataIndex: 'soldToCode',
            key: 'soldToCode',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: ({ confirm }: { confirm: () => void }) => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.soldTo} value={filters.soldTo} onSelectChange={handelSelectChangeFilter} onChange={() => {}} location={null} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('soldTo') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.soldToName'),
            dataIndex: 'soldToName',
            key: 'soldToName',
            ellipsis: true,
            showSorterTooltip: false
        },
        {
            title: t('order.shipToCode'),
            dataIndex: 'shipToCode',
            key: 'shipToCode',
            ellipsis: true,
            showSorterTooltip: false,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.shipTo} value={filters.shipTo} onSelectChange={handelSelectChangeFilter} onChange={() => {}} location={null} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('shipTo') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.shipToName'),
            dataIndex: 'shipToName',
            key: 'shipToName',
            ellipsis: true,
            showSorterTooltip: false,
        },
        {
            title: t('general.status'),
            dataIndex: 'orderStatusName',
            key: 'orderStatusName',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <Select
                        size='small'
                        style={{ width: "100%" }}
                        showSearch
                        allowClear
                        value={filters.orderStatus}
                        onChange={(val, option) => {
                            handelFilterChange("orderStatus", val);
                        }}
                        placeholder={t('general.select')}
                        optionFilterProp="label"
                        options={orderStatuses.map((item: any) => ({ value: item.id, label: item.description }))}
                    >
                    </Select>

                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('orderStatus') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.salesDocumentType'),
            dataIndex: 'salesDocumentType',
            key: 'salesDocumentType',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.salesOrganization'),
            dataIndex: 'salesOrganizationName',
            key: 'salesOrganizationName',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.salesOrganization} value={filters.salesOrganization} onSelectChange={handelSelectChangeFilter} onChange={() => {}} location={null} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('salesOrganization') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.material'),
            dataIndex: 'material',
            key: 'material',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.material} value={filters.material} onSelectChange={handelSelectChangeFilter} onChange={() => {}} location={null} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('material') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.poNumber'),
            dataIndex: 'poNumber',
            key: 'poNumber',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <Space>
                        <Input placeholder='Search' value={searchPoNumberText as any} size='small' allowClear onClear={() => handelFilterChange("poNumber", null)} onChange={(e: any) => setSearchPoNumberText(e.target.value)} />
                        <Button type='dashed' size='small' icon={<SearchOutlined />} onClick={(e) => handelFilterChange("poNumber", searchPoNumberText)}></Button>
                    </Space>
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('poNumber') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.salesOrderNumber'),
            dataIndex: 'salesOrderNumber',
            key: 'salesOrderNumber',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <Space>
                        <Input placeholder='Search' value={searchSalesOrderNumberText as any} size='small' allowClear onClear={() => handelFilterChange("salesOrderNumber", null)} onChange={(e: any) => setSearchSalesOrderNumberText(e.target.value)} />
                        <Button type='dashed' size='small' icon={<SearchOutlined />} onClick={(e) => handelFilterChange("salesOrderNumber", searchSalesOrderNumberText)}></Button>
                    </Space>
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('salesOrderNumber') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.reqDeliveryDate'),
            dataIndex: 'reqDeliveryDate',
            key: 'reqDeliveryDate',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <RangePicker
                        value={filters.reqDeliveryDateRange}
                        style={{ width: "100%" }}
                        format={dateFormat}
                        allowClear={true}
                        size='small'
                        placeholder={[t('general.startDate'), t('general.endDate')]}
                        onChange={(dates) => handelFilterChange("reqDeliveryDateRange", dates)}
                    />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('reqDeliveryDateRange') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            ),
            render: (text: any) => (text ? dayjs(text).format(dateFormat) : ""),
        },
        {
            title: t('order.calendarDay'),
            dataIndex: 'calendarDay',
            key: 'calendarDay',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DatePicker
                        // value={filters.calendarDay_GIDate}
                        style={{ width: "100%" }}
                        format={dateFormat}
                        allowClear={true}
                        size='small'
                        placeholder={t('general.select')}
                        autoComplete="off"
                        onChange={(date) => handelFilterChange("calendarDay_GIDate", date)}
                    />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('calendarDay_GIDate') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            ),
            render: (text: any) => (text ? dayjs(text).format(dateFormat) : ""),
        },
        {
            title: t('order.confDeliveryDate'),
            dataIndex: 'confDeliveryDate',
            key: 'confDeliveryDate',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <RangePicker
                        value={filters.confDeliveryDateRange}
                        style={{ width: "100%" }}
                        format={dateFormat}
                        allowClear={true}
                        size='small'
                        placeholder={[t('general.startDate'), t('general.endDate')]}
                        onChange={(dates) => handelFilterChange("confDeliveryDateRange", dates)}
                    />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('confDeliveryDateRange') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            ),
            render: (text: any) => (text ? dayjs(text).format(dateFormat) : ""),
        },
        {
            title: t('order.materialType'),
            dataIndex: 'materialType',
            key: 'materialType',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.materialType} value={filters.materialType} onSelectChange={handelSelectChangeFilter} onChange={() => {}} location={null} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('materialType') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            ),
        },
        {
            title: t('order.installationCode'),
            dataIndex: 'installationCode',
            key: 'installationCode',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.brand'),
            dataIndex: 'brand',
            key: 'brand',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.model'),
            dataIndex: 'model',
            key: 'model',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.productGroup'),
            dataIndex: 'productGroupDescription',
            key: 'productGroupDescription',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.quantity'),
            dataIndex: 'qty',
            key: 'qty',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.internalNotes'),
            dataIndex: 'internalNotes',
            key: 'internalNotes',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.overallCreditStatus'),
            dataIndex: 'overallCreditStatus',
            key: 'overallCreditStatus',
            showSorterTooltip: false,
            ellipsis: true,
        },
        {
            title: t('order.deliveryBlockHeader'),
            dataIndex: 'deliveryBlockHeader',
            key: 'deliveryBlockHeader',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.deliveryBlockScheduleLine'),
            dataIndex: 'deliveryBlockScheduleLine',
            key: 'deliveryBlockScheduleLine',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.billingBlockHeader'),
            dataIndex: 'billingBlockHeader',
            key: 'billingBlockHeader',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.storageLocation'),
            dataIndex: 'storageLocation',
            key: 'storageLocation',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.storageLocation} value={filters.storageLocation} onSelectChange={handelSelectChangeFilter} onChange={() => {}} location={null} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: (activeFilteredColumn as any).includes('storageLocation') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            ),
        },
        {
            title: t('order.plant'),
            dataIndex: 'plantKey',
            key: 'plantKey',
            ellipsis: true,
            showSorterTooltip: false
        },
        {
            title: t('order.creationDate'),
            dataIndex: 'creationDate',
            key: 'creationDate',
            showSorterTooltip: false,
            ellipsis: true,
            render: (text: any) => (text ? dayjs(text).format(dateFormat) : ""),
        },
        {
            title: t('order.openSalesExclVisFeeFreight'),
            dataIndex: 'openSalesExclVisFee_Freight',
            key: 'openSalesExclVisFee_Freight',
            showSorterTooltip: false,
            ellipsis: true
        }
    ];

    const CountrySelectionForRpaFilesGenerationModal = ({ visibility, onSave }: { visibility: boolean, onSave: () => void }) => {
        return (
            <Modal
                open={visibility}               
                maskClosable={false}
                onCancel={() => {
                    onRpaCountryModalCancel();
                }}
                footer={[
                    <Button key="back" onClick={() => onRpaCountryModalCancel()} disabled={loadingRpaFiles}>
                        {t('general.cancel')}
                    </Button>,
                    <Button htmlType="submit" key="submit" form='rpaCountries' style={{ background: "#011e41", color: "#ffffff" }} loading={loadingRpaFiles} onClick={onSave}>
                        {t('general.continue')}
                    </Button>
                ]}>

                <Result
                    status="warning"
                    style={{fontSize: 12}}
                    title={t('order.continueWithRpaFilesGeneration')}
                    icon={<ExclamationCircleOutlined style={{ fontSize: 35 }} />}                   
                />

                {/* <Form
                    layout='vertical'
                    id="rpaCountries"
                    autoComplete="off"
                    form={form}
                    style={{ minHeight: "200px" }}
                >

                    <Row gutter={16}>
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('users.country')}
                                name="countries"
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Select
                                    mode="multiple"
                                    style={{ width: '100%' }}
                                    optionFilterProp="label"
                                    disabled={loadingRpaFiles}
                                    showSearch
                                    allowClear
                                    placeholder={t('general.select')}
                                    options={userCountries.map((item: any) => ({ value: item.id, label: getCountryLabel(item) }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                </Form> */}
            </Modal>
        )
    }

    const onSendSelectedOrders = async () => {
        if (!selectedOrderIds.length) {
            message.warning(t('messages.noOrdersSelected'));
            return;
        }
        onMultiDeleteOrders()
    };

    const rowSelection = {
        selectedRowKeys,
        columnTitle: ' ',
        hideSelectAll: true,
        renderCell: (checked: any, record: any, index: number, originNode: any) => {
            if (record.orderStatusCode === orderStatusesCode.sendToRpa) {
                return null; // hide checkbox for send to RPA rows
            }
            return originNode; // default checkbox
        },
        preserveSelectedRowKeys: true,
        onChange: (newSelectedRowKeys: any) => {
            //update selected order header ids     
            setSelectedRowKeys(newSelectedRowKeys);
            const ids = (newSelectedRowKeys || []).map((k: any) => rowKeyToOrderIdRef.current.get(k)).filter(Boolean);
            const uniqueIds = Array.from(new Set(ids));

            // update selected item ids (orderItemsID values) - keep all orderItemsIDs, no deduplication
            setSelectedOrderIds(uniqueIds as any);
            const orderItemsIds = (newSelectedRowKeys || []).map((k: any) => rowKeyToOrderItemsIdRef.current.get(k)).filter(Boolean);
            setSelectedItemIds(orderItemsIds as any);
        }
    };

    const afterOrderListUpdate = () => {
        refreshOrders();
        setSelectedOrderIds([]);
        setSelectedItemIds([]);
        setSelectedRowKeys([]);
    }

    const closeResultModal = () => {
        setResultModal({
            open: false,
            status: 'info',
            title: '',
            subTitle: '',
        });
    };

    return (
        <div className="customerOrders">

            <ResultModal
                open={resultModal.open}
                status={resultModal.status}
                title={resultModal.title}
                subTitle={resultModal.subTitle}
                onClose={closeResultModal}
            />

            <CountrySelectionForRpaFilesGenerationModal
                visibility={rpaCountryModal}
                onSave={generateRpaFiles}
            />

            <UploadOrderModal
                visibility={uploadOrderModal}
                onClose={() => setUploadOrderModal(false)}
            />

            <MultiUpdateOrders
                visibility={multiUpdateOrdersModal}
                onClose={() => setMultiUpdateOrdersModal(false)}
                selectedOrderIds={selectedOrderIds}
                selectedItemIds={selectedItemIds}
                afterOrderListUpdate={afterOrderListUpdate}
            />

            <Card
                title={t('order.orders')}
                extra={
                    <Space direction='horizontal'>
                        <Can permission={permissions.UPLOAD_ORDER_FILE_BUTTON}>
                            <Button onClick={() => setUploadOrderModal(true)} icon={<UploadOutlined />}>{t('order.upoloadOrders')}</Button>
                        </Can>
                        <Button type='dashed' onClick={() => onClearFilters()}>{t('general.clear')} {t('general.filters')}</Button>

                        {/* <Can permission={permissions.CREATE_D2C_ORDER}>
                        <Button onClick={() => navigate(menuLinks.d2cOrder)} icon={<PlusOutlined />} type="default">{t('order.newD2cOrder')}</Button>
                        </Can> */}

                        <Can permission={permissions.UPDATE_ORDER}>
                            <Button onClick={() => setMultiUpdateOrdersModal(true)} disabled={!selectedOrderIds.length} icon={<EditOutlined />}>{t('order.updateSelected')}</Button>
                        </Can>

                        <Can permission={permissions.DELETE_ORDER}>
                            <Popconfirm
                                placement="topRight"
                                key="remove"
                                title={t('messages.confirmationApprove')}
                                icon={<WarningOutlined style={{ color: '#f7c924' }} />}
                                okText={t('general.yes')}
                                cancelText={t('general.no')}
                                onConfirm={onSendSelectedOrders}
                            >
                                <Button icon={<DeleteOutlined />} type='dashed' disabled={!selectedOrderIds.length} loading={multiDeleteOrdersMutation.isPending}>
                                    {t('order.removeSelected')}
                                </Button>
                            </Popconfirm>
                        </Can>

                    </Space>
                }
            >

                <Row gutter={{ lg: 24 }} className='mb10'>
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} style={{ padding: 0 }}>
                        <Table
                            size='small'
                            className='ordersTable'
                            columns={columns as any}
                            // dataSource={orderList}
                            dataSource={orderData?.orders ?? []}
                            rowKey="rowKey"
                            rowSelection={rowSelection}
                            loading={loadingOrderList}
                            bordered
                            onChange={(pagination, filters, sorter) =>
                                onPageOrOrderChange(pagination, filters, sorter)
                            }
                            pagination={{
                                pageSize: tablePageSize,
                                showSizeChanger: false,
                                current: pageNumber,
                                // total: totalCountRecords || 0
                                total: orderData?.total ?? 0
                            }}
                            scroll={{ x: 'max-content' }}
                        />
                    </Col>
                </Row>

                <Row gutter={{ lg: 24 }} className='mb10'>
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                        <Can permission={permissions.GENERATE_RPA_FILES}>
                            <Button loading={loadingRpaFiles} type='primary' onClick={() => onGenerateRpaFilesClick()} icon={<MailOutlined />}>{t('order.generateRpaFiles')}</Button>
                        </Can>
                    </Col>
                </Row>
            </Card>
        </div>
    );
}

export default CustomerOrders;